import type { ReactNode } from 'react';
import { useMe, useSignOut } from '../api/auth';
import { useFreshness } from '../api/hooks';
import { type Readiness, WINDOW_OPTIONS, type WindowDays } from '../api/types';
import { formatDay, formatUtcTime, isFirstPass, isStaleAsOf, windowReadiness } from '../lib/format';
import { Link, type ScreenKey, screenPath, useLocation } from '../lib/router';

const SCREENS: Array<[ScreenKey, string, string]> = [
  ['market', 'Market', 'Market'],
  ['genres', 'Genres', 'Genres'],
  ['island', 'Island detail', 'Island'],
  ['concepts', 'Concepts', 'Concepts'],
  ['mine', 'My islands', 'My islands'],
  ['guide', 'Ship your island', 'Guide'],
];

export function Shell({ children }: { children: ReactNode }) {
  const { route } = useLocation();
  const nav = (short: boolean) =>
    SCREENS.map(([key, label, shortLabel]) => (
      <Link key={key} to={screenPath(key)} aria-current={route.screen === key ? 'page' : undefined}>
        {short ? shortLabel : label}
      </Link>
    ));
  return (
    <div className="app">
      <aside className="side">
        <div className="brand">
          <i />
          Island Studio
        </div>
        <nav className="nav" aria-label="Screens">
          {nav(false)}
          <div className="sep" />
          <SettingsLink />
        </nav>
        <SignedInAs />
      </aside>
      <div style={{ minWidth: 0 }}>
        <header className="top">
          <div className="brand" style={{ padding: 0 }}>
            <i />
            Island Studio
          </div>
          <SettingsLink className="top-settings" />
        </header>
        <main className="main">{children}</main>
      </div>
      <nav className="tabbar" aria-label="Screens (tab bar)" data-testid="tabbar">
        {nav(true)}
      </nav>
    </div>
  );
}

/** Bottom of the sidebar: who is signed in, and the way out. */
function SignedInAs() {
  const me = useMe();
  const signOut = useSignOut();
  if (!me.data) return null;
  return (
    <div className="who" data-testid="signed-in-as">
      <span className="muted">Signed in as</span>
      <b>{me.data.username}</b>
      <button type="button" className="linklike" onClick={() => void signOut()}>
        Sign out
      </button>
    </div>
  );
}

/** Settings sits under a separator in the sidebar; on a phone it is in the top bar (the tab bar has no room). */
function SettingsLink({ className }: { className?: string }) {
  const { route } = useLocation();
  return (
    <Link
      to={screenPath('settings')}
      className={className}
      aria-current={route.screen === 'settings' ? 'page' : undefined}
    >
      Settings
    </Link>
  );
}

/** "Snapshot 5 Oct 2026 · covers through 5 Oct (UTC) · ingest ok 05:30 UTC" with a status dot. */
export function Freshness({ apiError }: { apiError?: boolean }) {
  const f = useFreshness();
  if (f.isLoading) return <span className="fresh">Checking freshness…</span>;
  const d = f.data;
  if (!d) {
    return (
      <span className="fresh" data-testid="freshness">
        <span className="dot bad" />
        Freshness unknown: the API did not answer
      </span>
    );
  }
  const stale = d.as_of === null || isStaleAsOf(d.as_of, d.today);
  const ingest = d.last_ingest;
  const ingestOk = ingest !== null && (ingest.status === 'ok' || ingest.status === 'partial');
  const down = apiError || f.showingLastGood;
  const dot = down ? 'bad' : stale || !ingestOk ? 'warn' : '';
  const published = d.published_at ? d.published_at.slice(0, 10) : d.as_of;
  return (
    <span className="fresh" data-testid="freshness">
      <span className={`dot ${dot}`} />
      {down ? 'Showing last good snapshot · ' : ''}
      {d.as_of === null ? (
        'No snapshot published yet'
      ) : (
        <>
          Snapshot {formatDay(published)} · covers through {formatDay(d.as_of, { year: false })} (UTC)
          {ingest ? (
            <>
              {' '}
              · ingest {ingest.status} {formatUtcTime(ingest.finishedAt)}
            </>
          ) : null}
        </>
      )}
      {stale && d.as_of !== null ? <span className="pill p-warn">stale</span> : null}
    </span>
  );
}

/** Readiness of every window, computed exactly as the API does (windowReadiness over as_of/history_start). */
export function useWindowReadiness(): Record<WindowDays, Readiness> | null {
  const f = useFreshness();
  if (!f.data) return null;
  const { as_of, history_start } = f.data;
  return {
    1: windowReadiness(1, as_of, history_start),
    7: windowReadiness(7, as_of, history_start),
    30: windowReadiness(30, as_of, history_start),
    90: windowReadiness(90, as_of, history_start),
  };
}

/** 1d · 7d · 30d · 90d chips; a collecting window shows "have/need" days (no count during the first pass). */
export function WindowChips({ value, onChange }: { value: WindowDays; onChange: (w: WindowDays) => void }) {
  const ready = useWindowReadiness();
  return (
    <fieldset className="chips" aria-label="Time window" data-testid="window-chips">
      {WINDOW_OPTIONS.map((w) => {
        const r = ready?.[w];
        return (
          <button
            type="button"
            key={w}
            aria-pressed={value === w}
            onClick={() => onChange(w)}
            data-window={w}
            title={r && isFirstPass(r) ? 'First catalog pass still running' : undefined}
          >
            {w}d
            {r && r.status === 'collecting' && !isFirstPass(r) ? (
              <small title={`collecting: ${r.have} of ${r.need} days stored`}>
                {' '}
                {r.have}/{r.need}
              </small>
            ) : null}
          </button>
        );
      })}
    </fieldset>
  );
}

export function parseWindow(v: string | null, fallback: WindowDays = 7): WindowDays {
  const n = Number(v);
  return (WINDOW_OPTIONS as readonly number[]).includes(n) ? (n as WindowDays) : fallback;
}
